import { DocusealForm } from "@docuseal/react";
import { useMemo, useState, useEffect } from "react";
import { Home, RefreshCw, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import OnScreenKeyboard from "../components/Common/OnScreenKeyboard";
import CategoryHeading from "../components/Common/CategoryHeading";
import "../styles/WaiverForm.css";

export default function WaiverKiosk() {
  /* unique tag each mount → fresh submission every time */
  const tag = useMemo(
    () => crypto.randomUUID?.() || Date.now().toString(36),
    []
  );

  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [parentGuardianInput, setParentGuardianInput] = useState("");
  const [ageVerification, setAgeVerification] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Get current date in MM/DD/YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const year = today.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleComplete = () => {
    setCompleted(true);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (nameInput.trim() && ageVerification) {
      if (ageVerification === 'under18' && !parentGuardianInput.trim()) {
        return; // Require parent/guardian for under 18
      }

      try {
        // Submit full name as separate record
        const nameResponse = await fetch('https://names-storage.t9e2i3kal.workers.dev/add', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ value: nameInput.trim() })
        });

        if (!nameResponse.ok) {
          console.error('Failed to submit name:', nameResponse.statusText);
        } else {
          const result = await nameResponse.json();
          console.log('Name submitted successfully:', result);
        }

        // Submit guardian name as separate record if provided
        if (parentGuardianInput.trim()) {
          const guardianResponse = await fetch('https://names-storage.t9e2i3kal.workers.dev/add', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ value: parentGuardianInput.trim() })
          });

          if (!guardianResponse.ok) {
            console.error('Failed to submit guardian name:', guardianResponse.statusText);
          } else {
            const result = await guardianResponse.json();
            console.log('Guardian name submitted successfully:', result);
          }
        }
      } catch (error) {
        console.error('Error submitting names:', error);
      }

      setShowForm(true);
      setLoading(true);
    }
  };

  const handleNameChange = (e) => {
    setNameInput(e.target.value);
  };

  const handleParentGuardianChange = (e) => {
    setParentGuardianInput(e.target.value);
  };

  const handleAgeVerificationChange = (e) => {
    setAgeVerification(e.target.value);
  };

  const handleBackToName = () => {
    setShowForm(false);
    setLoading(false);
  };

  if (completed) {
    return (
      <div className="waiver-form-container">
        <CategoryHeading title="Thank You" description="Waiver Successfully Completed" />

        <main className="waiver-form-content">
          <div className="waiver-thank-you-container">
            <div className="waiver-thank-you-content">
              <p>Thank you for completing the liability waiver.</p>

              <div className="waiver-thank-you-buttons">
                <button
                  className="waiver-thank-you-button"
                  onClick={handleGoHome}
                  title="Go to Home"
                >
                  <Home size={24} />
                  <span>Home</span>
                </button>

                <button
                  className="waiver-thank-you-button"
                  onClick={handleRefresh}
                  title="Refresh Page"
                >
                  <RefreshCw size={24} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </main>

        <footer className="waiver-form-footer">
          <p>
            Your waiver has been successfully submitted. Enjoy your experience!
          </p>
        </footer>
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className="waiver-form-container">
        <CategoryHeading
          title="Event Waiver"
        />

        <main className="waiver-form-content">
          <div className="waiver-name-input-container">
            <div className="waiver-name-input-content">
              <div className="waiver-name-input-icon">
                <User size={48} />
              </div>
              <p>Please enter your full name to begin the waiver process</p>

              <form onSubmit={handleNameSubmit} className="waiver-name-form">
                <div className="waiver-input-group">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={handleNameChange}
                    placeholder="Enter your full name"
                    className="waiver-name-input"
                    required
                    autoFocus
                    autoComplete="name"
                    inputMode="text"
                    enterKeyHint="next"
                  />
                </div>

                <div className="age-verification-group">
                  <h3>Age Verification</h3>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="ageVerification"
                        value="18orOlder"
                        checked={ageVerification === "18orOlder"}
                        onChange={handleAgeVerificationChange}
                        required
                      />
                      <span>I am 18 years of age or older</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="ageVerification"
                        value="under18"
                        checked={ageVerification === "under18"}
                        onChange={handleAgeVerificationChange}
                        required
                      />
                      <span>I am under the age of 18</span>
                    </label>
                  </div>
                </div>

                {ageVerification === "under18" && (
                  <div className="waiver-input-group">
                    <input
                      type="text"
                      value={parentGuardianInput}
                      onChange={handleParentGuardianChange}
                      placeholder="Parent/Guardian name (required)"
                      className="waiver-name-input"
                      required
                      autoComplete="name"
                      inputMode="text"
                      enterKeyHint="go"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  className="waiver-continue-button"
                  disabled={
                    !nameInput.trim() ||
                    !ageVerification ||
                    (ageVerification === "under18" && !parentGuardianInput.trim())
                  }
                >
                  Continue to Waiver
                </button>
              </form>
            </div>
          </div>
        </main>

        <footer className="waiver-form-footer">
          <p>
            Your name will be used to personalize the liability waiver document
          </p>
        </footer>
        {!isMobile && <OnScreenKeyboard />}
      </div>
    );
  }

  return (
    <div className="waiver-form-container">
      <CategoryHeading
        title={`Liability Waiver - ${nameInput}`}
        description="Please read and sign the waiver below"
      />
      <button
        onClick={handleBackToName}
        className="waiver-back-button"
        title="Change Name"
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 1000
        }}
      >
        <User size={16} />
        Change Name
      </button>

      <main className="waiver-form-content">
        {loading && (
          <div className="waiver-loading-container">
            <div className="waiver-loading-spinner"></div>
            <div className="waiver-loading-text">Loading Waiver Form...</div>
            <div className="waiver-loading-subtext">
              Please wait while we prepare your document
            </div>
          </div>
        )}

        <div className="waiver-docuseal-container">
          <DocusealForm
            src="https://docuseal.com/d/fkwT8zFKD2e8ds"
            email={`${tag}@kiosk.local`}
            name={nameInput.trim().toUpperCase()}
            externalId={tag}
            values={{
              "Full Name": nameInput.trim().toUpperCase(),
              Name: nameInput.trim().toUpperCase(),
              "Participant Name": nameInput.trim().toUpperCase(),
              "First Name":
                nameInput.trim().split(" ")[0]?.toUpperCase() ||
                nameInput.trim().toUpperCase(),
              "Last Name":
                nameInput.trim().split(" ").slice(1).join(" ").toUpperCase() ||
                "",
              "Parent/Guardian Name": parentGuardianInput.trim().toUpperCase(),
              "Date Signed": getCurrentDate(),
              "Parent/Guardian Date": getCurrentDate(),
            }}
            sendCopyEmail={false}
            withSendCopyButton={false}
            withDownloadButton={false}
            rememberSignature={false}
            allowToEditEmail={false}
            withConfetti={true}
            goToLast={false}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              visibility: loading ? "hidden" : "visible",
              touchAction: "manipulation",
              pointerEvents: "auto",
            }}
            onInit={() => {
              setLoading(false);
              console.log("DocuSeal form initialized");
            }}
            onLoad={() => {
              setLoading(false);
              console.log("DocuSeal form loaded");

              setTimeout(() => {
                const iframe = document.querySelector(
                  'iframe[src*="docuseal.com"]'
                );
                if (iframe) {
                  iframe.setAttribute(
                    "allow",
                    "geolocation; microphone; camera; midi; vr; accelerometer; gyroscope; payment; ambient-light-sensor; encrypted-media; usb; clipboard-read; clipboard-write"
                  );
                  iframe.setAttribute(
                    "sandbox",
                    "allow-same-origin allow-scripts allow-forms allow-popups allow-presentation allow-downloads allow-top-navigation-by-user-activation"
                  );
                  iframe.style.touchAction = "manipulation";
                  iframe.style.pointerEvents = "auto";
                  iframe.style.userSelect = "text";
                  iframe.style.webkitUserSelect = "text";
                  iframe.setAttribute("data-keyboard-enabled", "true");
                  iframe.setAttribute("data-kiosk-mode", "true");
                  iframe.tabIndex = 0;
                  console.log("Enhanced iframe configuration applied");
                }
              }, 100);
            }}
            onComplete={handleComplete}
            onError={(error) => {
              console.error("DocuSeal form error:", error);
              setLoading(false);
            }}
            metadata={{
              kiosk_mode: true,
              virtual_keyboard: true,
              source: "kiosk",
              participant_name: nameInput.trim().toUpperCase(),
              parent_guardian_name: parentGuardianInput.trim().toUpperCase(),
              date_signed: getCurrentDate(),
            }}
          />
        </div>
      </main>

      <footer className="waiver-form-footer">
        <p>
          Please read and sign the liability waiver before proceeding with your
          experience
        </p>
      </footer>
    </div>
  );
}
